//src/pages/SorteioLeadForm.jsx

import React, { useEffect, useState } from "react";
import axios from "../axiosConfig";
import { useNavigate, useParams } from "react-router-dom";
import { Plus, Save, Trash2 } from "lucide-react";
import "./SorteioLeadForm.css";

function SorteioLeadForm() {
  const { id } = useParams();
  const editando = Boolean(id);
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [mensagem, setMensagem] = useState("");

  const [form, setForm] = useState({
    slug: "",
    titulo: "",
    descricao: "",
    data_evento: "",
    inscricoes_inicio: "",
    inscricoes_fim: "",
    ativo: 1,
    status: "ativo",
    senha_apresentador: ""
  });

  const [premios, setPremios] = useState([
    { ordem: 1, nome_premio: "", descricao: "", imagem_url: "" }
  ]);

  useEffect(() => {
    if (!editando) return;

    setLoading(true);

    axios
      .get(`/admin/sorteios-leads/id/${id}`)
      .then(({ data }) => {
        const s = data.sorteio;

        setForm({
          slug: s.slug || "",
          titulo: s.titulo || "",
          descricao: s.descricao || "",
          data_evento: s.data_evento ? String(s.data_evento).slice(0, 10) : "",
          inscricoes_inicio: s.inscricoes_inicio ? String(s.inscricoes_inicio).slice(0, 16) : "",
          inscricoes_fim: s.inscricoes_fim ? String(s.inscricoes_fim).slice(0, 16) : "",
          ativo: Number(s.ativo) ? 1 : 0,
          status: s.status || "ativo",
          senha_apresentador: ""
        });

        setPremios(
          data.premios?.length
            ? data.premios.map((p) => ({
                ordem: p.ordem,
                nome_premio: p.nome_premio || "",
                descricao: p.descricao || "",
                imagem_url: p.imagem_url || "",
                sorteado: p.sorteado
              }))
            : [{ ordem: 1, nome_premio: "", descricao: "", imagem_url: "" }]
        );
      })
      .catch((err) => {
        setMensagem(err.response?.data?.error || "Erro ao carregar sorteio.");
      })
      .finally(() => setLoading(false));
  }, [editando, id]);

  const atualizarCampo = (campo, valor) => {
    setForm((prev) => ({ ...prev, [campo]: valor }));
  };

  const atualizarPremio = (index, campo, valor) => {
    setPremios((prev) =>
      prev.map((p, i) => (i === index ? { ...p, [campo]: valor } : p))
    );
  };

  const adicionarPremio = () => {
    setPremios((prev) => [
      ...prev,
      {
        ordem: prev.length + 1,
        nome_premio: "",
        descricao: "",
        imagem_url: ""
      }
    ]);
  };

  const removerPremio = (index) => {
    setPremios((prev) =>
      prev
        .filter((_, i) => i !== index)
        .map((p, i) => ({ ...p, ordem: i + 1 }))
    );
  };

  const salvar = async (e) => {
    e.preventDefault();
    setMensagem("");

    if (!form.slug || !form.titulo) {
      setMensagem("Informe slug e título.");
      return;
    }

    if (!editando && !form.senha_apresentador) {
      setMensagem("Informe a senha do apresentador.");
      return;
    }

    const premiosValidos = premios
      .filter((p) => p.nome_premio.trim())
      .map((p, index) => ({
        ...p,
        ordem: index + 1
      }));

    if (!premiosValidos.length) {
      setMensagem("Cadastre pelo menos um prêmio.");
      return;
    }

    setLoading(true);

    try {
      if (editando) {
        await axios.put(`/admin/sorteios-leads/${id}`, form);
        await axios.put(`/admin/sorteios-leads/${id}/premios`, {
          premios: premiosValidos
        });
      } else {
        await axios.post("/admin/sorteios-leads", {
          ...form,
          premios: premiosValidos
        });
      }

      navigate("/admin/sorteios-leads");
    } catch (err) {
      setMensagem(err.response?.data?.error || "Erro ao salvar sorteio.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="sorteioLeadForm-page">
      <header className="sorteioLeadForm-header">
        <span className="sorteioLeadForm-kicker">Sorteios Tikpag</span>
        <h1>{editando ? "Editar sorteio" : "Novo sorteio"}</h1>
        <p>
          Configure o link público, período de inscrições, senha do apresentador
          e sequência de prêmios.
        </p>
      </header>

      {mensagem && <div className="sorteioLeadForm-alert">{mensagem}</div>}

      <form onSubmit={salvar} className="sorteioLeadForm-card">
        <div className="sorteioLeadForm-section">
          <h2>Dados do sorteio</h2>

          <div className="sorteioLeadForm-grid">
            <label>
              <span>Slug público</span>
              <input
                value={form.slug}
                onChange={(e) =>
                  atualizarCampo(
                    "slug",
                    e.target.value
                      .toLowerCase()
                      .replace(/[^a-z0-9-]/g, "-")
                      .replace(/-+/g, "-")
                  )
                }
                placeholder="evento-tikpag"
              />
            </label>

            <label>
              <span>Título</span>
              <input
                value={form.titulo}
                onChange={(e) => atualizarCampo("titulo", e.target.value)}
                placeholder="Sorteio Evento Tikpag"
              />
            </label>

            <label className="sorteioLeadForm-full">
              <span>Descrição</span>
              <textarea
                value={form.descricao}
                onChange={(e) => atualizarCampo("descricao", e.target.value)}
                placeholder="Texto que aparecerá na página pública"
              />
            </label>

            <label>
              <span>Data do evento</span>
              <input
                type="date"
                value={form.data_evento}
                onChange={(e) => atualizarCampo("data_evento", e.target.value)}
              />
            </label>

            <label>
              <span>Inscrições início</span>
              <input
                type="datetime-local"
                value={form.inscricoes_inicio}
                onChange={(e) => atualizarCampo("inscricoes_inicio", e.target.value)}
              />
            </label>

            <label>
              <span>Inscrições fim</span>
              <input
                type="datetime-local"
                value={form.inscricoes_fim}
                onChange={(e) => atualizarCampo("inscricoes_fim", e.target.value)}
              />
            </label>

            <label>
              <span>Status</span>
              <select
                value={form.status}
                onChange={(e) => atualizarCampo("status", e.target.value)}
              >
                <option value="rascunho">Rascunho</option>
                <option value="ativo">Ativo</option>
                <option value="encerrado">Encerrado</option>
              </select>
            </label>

            <label>
              <span>Ativo</span>
              <select
                value={form.ativo}
                onChange={(e) => atualizarCampo("ativo", Number(e.target.value))}
              >
                <option value={1}>Sim</option>
                <option value={0}>Não</option>
              </select>
            </label>

            <label>
              <span>{editando ? "Nova senha do apresentador" : "Senha do apresentador"}</span>
              <input
                type="password"
                value={form.senha_apresentador}
                onChange={(e) => atualizarCampo("senha_apresentador", e.target.value)}
                placeholder={editando ? "Deixe vazio para manter" : "Senha para acessar o sorteador"}
              />
            </label>
          </div>
        </div>

        <div className="sorteioLeadForm-section">
          <div className="sorteioLeadForm-sectionTitle">
            <h2>Prêmios em sequência</h2>

            <button type="button" onClick={adicionarPremio}>
              <Plus size={16} />
              Adicionar prêmio
            </button>
          </div>

          <div className="sorteioLeadForm-premios">
            {premios.map((premio, index) => (
              <div className="sorteioLeadForm-premio" key={index}>
                <strong>#{index + 1}</strong>

                <input
                  value={premio.nome_premio}
                  onChange={(e) => atualizarPremio(index, "nome_premio", e.target.value)}
                  placeholder="Nome do prêmio"
                  disabled={Number(premio.sorteado) === 1}
                />

                <input
                  value={premio.descricao}
                  onChange={(e) => atualizarPremio(index, "descricao", e.target.value)}
                  placeholder="Descrição opcional"
                  disabled={Number(premio.sorteado) === 1}
                />

                <input
                  value={premio.imagem_url}
                  onChange={(e) => atualizarPremio(index, "imagem_url", e.target.value)}
                  placeholder="URL da imagem opcional"
                  disabled={Number(premio.sorteado) === 1}
                />

                <button
                  type="button"
                  onClick={() => removerPremio(index)}
                  disabled={premios.length === 1 || Number(premio.sorteado) === 1}
                  title="Remover prêmio"
                >
                  <Trash2 size={15} />
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className="sorteioLeadForm-actions">
          <button type="button" onClick={() => navigate("/admin/sorteios-leads")}>
            Cancelar
          </button>

          <button type="submit" disabled={loading}>
            <Save size={16} />
            {loading ? "Salvando..." : "Salvar sorteio"}
          </button>
        </div>
      </form>
    </div>
  );
}

export default SorteioLeadForm;